import { APP_INITIALIZER, ApplicationConfig, ErrorHandler, importProvidersFrom } from '@angular/core';
import { provideRouter, PreloadAllModules, PreloadingStrategy, withInMemoryScrolling } from '@angular/router';
import { GlobalErrorHandler } from './utilities/services/loading/global-error-handler';
import { routes } from './app.routes';
import {
  provideClientHydration,
  withNoHttpTransferCache,
} from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideHttpClient, withFetch, HttpClient } from '@angular/common/http';
import { WebsocketService } from './utilities/services/websocket-service/websocket.service';
import { provideAnimations } from '@angular/platform-browser/animations';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { ANALYTICS_PROVIDERS } from './utilities/services/analytics/analytics.tokens';
import { GoogleAnalyticsProvider } from './utilities/services/analytics/google-analytics.provider';
import { TenantLabelOverrideService } from './utilities/services/i18n/tenant-label-override.service';
import { provideSentryTracing } from './utilities/monitoring/sentry/sentry.providers';

/**
 * Factory for the tenant label override APP_INITIALIZER — registers this
 * tenant's display-text overrides before the app renders so no component
 * ever flashes the generic locale-file text first.
 * @param tenantLabelOverrideService Service that fetches and applies the overrides.
 * @returns A function returning a promise Angular awaits before bootstrapping.
 */
function initializeTenantLabelOverrides(
  tenantLabelOverrideService: TenantLabelOverrideService
): () => Promise<void> {
  return () => tenantLabelOverrideService.loadAndApplyOverrides();
}


class CustomTranslateLoader implements TranslateLoader {
  constructor(private readonly http: HttpClient) {}

  getTranslation(lang: string): Observable<any> {
    return this.http.get(`/assets/i18n/${lang}.json`);
  }
}

export function createTranslateLoader(http: HttpClient): TranslateLoader {
  return new CustomTranslateLoader(http);
}


export const appConfig: ApplicationConfig = {
  providers: [
    ...provideSentryTracing(),
    provideRouter(
      routes,
      withInMemoryScrolling({ scrollPositionRestoration: 'top' })
    ),
    { provide: PreloadingStrategy, useClass: PreloadAllModules },
    provideClientHydration(withNoHttpTransferCache()),
    provideAnimationsAsync(),
    provideHttpClient(withFetch()),
    provideAnimations(),
    WebsocketService,
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    // Analytics providers — add one line per provider (Azure, Mixpanel, ...).
    { provide: ANALYTICS_PROVIDERS, useExisting: GoogleAnalyticsProvider, multi: true },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeTenantLabelOverrides,
      deps: [TenantLabelOverrideService],
      multi: true,
    },
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: createTranslateLoader,
          deps: [HttpClient]
        },
        fallbackLang: 'en'
      })
    )
  ],
};
